import React, { useState, useEffect, useMemo, useRef } from "react";
import { Calendar as CalendarIcon } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar } from "./ui/calendar";
import { de } from "date-fns/locale";

/**
 * Controlled date field with a stable DD.MM.YYYY visible display.
 *
 * Internally:
 *   - props.value is ISO YYYY-MM-DD (or "" for empty).
 *   - props.onChange(iso) is called with YYYY-MM-DD or "" on clear.
 *
 * Visible input:
 *   - Always renders DD.MM.YYYY — never relies on native <input type="date"> rendering.
 *   - User can type DD.MM.YYYY directly or pick from the calendar popover.
 */
export function DateField({
  value,
  onChange,
  placeholder = "TT.MM.JJJJ",
  className = "",
  inputClassName = "",
  testId,
  title,
  label,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(() => isoToDe(value));
  const lastEmittedIso = useRef(value || "");

  // Keep the visible text in sync when the value prop changes externally
  // (e.g. when the dialog re-opens with a different project, or when sibling
  // logic auto-derives the due date from the invoice date).
  useEffect(() => {
    const next = isoToDe(value);
    setText((cur) => (cur === next ? cur : next));
    lastEmittedIso.current = value || "";
  }, [value]);

  const selectedDate = useMemo(() => isoToDate(value), [value]);

  function emit(iso) {
    const normalized = iso || "";
    if (normalized === lastEmittedIso.current) return;
    lastEmittedIso.current = normalized;
    onChange(normalized);
  }

  function handleInputChange(raw) {
    // Allow only digits and dot while typing; cap length to 10 (DD.MM.YYYY).
    const cleaned = raw.replace(/[^\d.]/g, "").slice(0, 10);
    setText(cleaned);

    if (cleaned === "") {
      emit("");
      return;
    }
    const iso = deToIso(cleaned);
    if (iso) emit(iso);
  }

  function handleBlur() {
    // On blur, snap the visible text back to the canonical formatting of the
    // current value (e.g. "1.5.2026" → "01.05.2026"); clear when invalid.
    if (lastEmittedIso.current) {
      setText(isoToDe(lastEmittedIso.current));
    } else {
      setText("");
    }
  }

  function handleSelect(date) {
    if (!date) return;
    const iso = dateToIso(date);
    setText(isoToDe(iso));
    emit(iso);
    setOpen(false);
  }

  return (
    <div className={className}>
      {label && (
        <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1" data-testid={testId ? `${testId}-label` : undefined}>
          {label}
        </div>
      )}
      <div className="relative">
      <input
        type="text"
        inputMode="numeric"
        autoComplete="off"
        value={text}
        onChange={(e) => handleInputChange(e.target.value)}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        title={title}
        data-testid={testId}
        className={`flex h-9 w-full rounded-md border border-input bg-transparent pl-3 pr-9 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 tabular-nums ${inputClassName}`}
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            aria-label="Datum wählen"
            className="absolute inset-y-0 right-0 grid place-items-center w-9 text-slate-500 hover:text-slate-900 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid={testId ? `${testId}-picker` : undefined}
          >
            <CalendarIcon className="w-4 h-4" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="end">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={handleSelect}
            defaultMonth={selectedDate || undefined}
            weekStartsOn={1}
            locale={de}
            initialFocus
          />
        </PopoverContent>
      </Popover>
      </div>
    </div>
  );
}

/* ---------------- helpers ---------------- */

function isoToDe(iso) {
  if (!iso) return "";
  const s = String(iso).slice(0, 10);
  const parts = s.split("-");
  if (parts.length !== 3) return "";
  const [y, m, d] = parts;
  if (!y || !m || !d) return "";
  return `${d}.${m}.${y}`;
}

function deToIso(de) {
  // Accept "D.M.YYYY", "DD.M.YYYY", "DD.MM.YYYY"
  if (!de) return "";
  const m = de.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (!m) return "";
  const day = Number(m[1]);
  const mon = Number(m[2]);
  const year = Number(m[3]);
  if (!isValidYmd(year, mon, day)) return "";
  return `${String(year).padStart(4, "0")}-${String(mon).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function isValidYmd(y, m, d) {
  if (m < 1 || m > 12 || d < 1 || d > 31) return false;
  const probe = new Date(y, m - 1, d);
  return (
    probe.getFullYear() === y &&
    probe.getMonth() === m - 1 &&
    probe.getDate() === d
  );
}

function isoToDate(iso) {
  if (!iso) return undefined;
  const s = String(iso).slice(0, 10);
  const parts = s.split("-").map(Number);
  if (parts.length !== 3 || parts.some(Number.isNaN)) return undefined;
  return new Date(parts[0], parts[1] - 1, parts[2]);
}

function dateToIso(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

export default DateField;
